import { zodResolver } from "@hookform/resolvers/zod"
import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { useNavigate, Link } from "react-router-dom"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ROUTES } from "@/routes/path"
import { useMutation } from "@tanstack/react-query"
import { login } from "@/lib/http/auth"
import { ErrorCard } from "@/components/ui/error"
import { useOnAuthSuccess } from "@/hooks/useAuth"
import { useAuth, useSetEmail } from "@/hooks/useAuth"
import { useGoogleAuth } from "@/hooks/useGoogleAuth"
import { useGitHubAuth } from "@/hooks/useGitHubAuth"

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
})

type LoginFormValues = z.infer<typeof loginSchema>

export default function Login() {
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { email } = useAuth()
  const setEmail = useSetEmail()
  const { handleGoogleAuth, isLoading: googleLoading } = useGoogleAuth()
  const { handleGitHubAuth, isLoading: githubLoading } = useGitHubAuth()
  const onAuthSuccess = useOnAuthSuccess()

  const mutation = useMutation({
    mutationFn: async (data: LoginFormValues) => {
      setIsLoading(true)
      setFormError(null)
      return await login(data.email, data.password)
    },
    onSuccess: (data) => {
      if (data?.token) {
        onAuthSuccess(data)
        return
      }
      setFormError("invalid credentials.")
    },
    onError: (error: any) => {
      if (error?.response?.data?.error.toLowerCase() === "account is not verified, please verify account") {
        navigate(ROUTES.AUTH.CONFIRM)
        return
      }
      setIsLoading(false)
      setFormError(error?.response?.data?.error || "Invalid credentials.")
    },
  })

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, touchedFields },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: email || "",
    },
  })

  const emailValue = watch("email")

  useEffect(() => {
    setEmail(emailValue)
  }, [emailValue, setEmail])

  return (
    <div className="relative flex h-screen flex-col items-center bg-gray-50 dark:bg-zinc-900">
      <div className="relative z-10 flex w-full flex-col items-center">
        <div className="my-10 flex justify-center">
          <img src="/logo.svg" alt="Ledgerbeam" width={220} height={50} />
        </div>
        {/* Card */}
        <div className="mx-2 max-h-fit w-full max-w-lg rounded-xl bg-white p-8 shadow-xl dark:bg-zinc-800">
          <h1 className="mb-8 text-2xl font-semibold dark:text-zinc-100">Sign in to your account</h1>
          <form onSubmit={handleSubmit((data: LoginFormValues) => mutation.mutate(data))} className="space-y-6">
            <div className="grid gap-4">
              <Input
                id="email"
                placeholder="name@company.com"
                type="email"
                label="Email"
                disabled={isLoading}
                {...register("email")}
                error={errors.email?.message}
                touched={touchedFields.email}
              />
            </div>

            <div className="grid gap-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to={ROUTES.AUTH.FORGOT_PASSWORD}
                  className="text-xs text-primary hover:underline dark:text-primary"
                >
                  Forgot your password?
                </Link>
              </div>
              <Input
                id="password"
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                disabled={isLoading}
                {...register("password")}
                error={errors.password?.message}
                touched={touchedFields.password}
                suffix={
                  <button
                    type="button"
                    tabIndex={-1}
                    className="focus:outline-none"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-5 text-gray-400 hover:text-gray-500"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-5 text-gray-400 hover:text-gray-500"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                        />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    )}
                  </button>
                }
              />
            </div>
            {formError && <ErrorCard message={formError} />}
            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? "Signing in..." : "Sign in"}
            </Button>
            <div className="my-2 flex items-center">
              <div className="grow border-t dark:border-zinc-700" />
              <span className="mx-2 text-xs text-muted-foreground">OR</span>
              <div className="grow border-t dark:border-zinc-700" />
            </div>
            <div className="grid gap-4">
              <Button
                variant="outline"
                type="button"
                disabled={isLoading || googleLoading}
                onClick={handleGoogleAuth}
                className="flex w-full items-center justify-center"
              >
                {googleLoading ? (
                  <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  <img
                    src="https://logos.ledgerbeam.com/google.com"
                    alt="Google"
                    width={20}
                    height={20}
                    className="mr-2"
                  />
                )}
                Sign in with Google
              </Button>
              <Button
                variant="outline"
                type="button"
                disabled={isLoading || githubLoading}
                onClick={handleGitHubAuth}
                className="flex w-full items-center justify-center"
              >
                {githubLoading ? (
                  <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  <img
                    src="https://logos.ledgerbeam.com/github.com"
                    alt="GitHub"
                    width={20}
                    height={20}
                    className="mr-2"
                  />
                )}
                Sign in with GitHub
              </Button>
            </div>
          </form>
        </div>

        <div className="-mt-4 w-full max-w-lg rounded-b-xl border-t bg-gray-50 px-8 py-4 text-center text-sm text-muted-foreground shadow-md shadow-gray-200 dark:border-zinc-700 dark:bg-zinc-800 dark:shadow-zinc-700">
          New to Ledgerbeam?{" "}
          <Link to={ROUTES.AUTH.REGISTER} className="font-medium text-primary hover:underline dark:text-primary">
            Create account
          </Link>
        </div>
      </div>
    </div>
  )
}
